import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import type { AbilitiesEvaluation } from "vue-simple-acl/types/acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { canWithRedirection } from "~/core/utils/canWithRedirection";
import {
  PATH_BUSINESS_UNITS_ID,
  PATH_BUSINESS_UNITS_ID_SETTINGS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
  PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
  PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES_UUID,
  PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_DELEGATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
} from "~/modules/businessUnit/constants";
import BusinessUnitPage from "~/modules/businessUnit/views/BusinessUnitPage.vue";
import BusinessUnitSettingsPage from "~/modules/businessUnit/views/BusinessUnitSettingsPage.vue";
import { router } from "~/plugins/router";

const routes: RouteRecordRaw[] = [
  {
    path: PATH_BUSINESS_UNITS_ID,
    component: BusinessUnitPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_ORG),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_ORG),
    },
  },
  // Note: Do not use redirect here because the tab "Affiliation" can be not displayed.
  //       It will be replaced in the component RelationsComponent.
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
        params: { id: to.params.id, userLogin: to.params.userLogin },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_WRITE_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_DELEGATIONS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_WRITE_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_FEATURE),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES_UUID,
    component: BusinessUnitSettingsPage,
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_FEATURE),
    },
  },
];

export default routes;
